import { z } from "zod";
import { assignableProjectRoleSchema } from "./projects.ts";
import { entityIdSchema, userIdSchema } from "./tenancy.ts";

/**
 * Directory (openspec change `directory-and-groups`): groups, typed user attributes, attribute
 * rules and the sources that keep them aligned with the customer's own directory (IdP claims,
 * SCIM, push feeds, pull connectors). Groups and rules are reader principals of documents and
 * groups can hold project roles; every decision reads committed state, never a cache.
 */
export const groupIdSchema = z.string().brand<"GroupId">();
export const attributeRuleIdSchema = z.string().brand<"AttributeRuleId">();
export const directorySourceIdSchema = z.string().brand<"DirectorySourceId">();

export type GroupId = z.infer<typeof groupIdSchema>;
export type AttributeRuleId = z.infer<typeof attributeRuleIdSchema>;
export type DirectorySourceId = z.infer<typeof directorySourceIdSchema>;

// ── Google Workspace (openspec change google-workspace) ─────────────────────────────────────

/** The pull adapter reading a Google Workspace directory through the Admin SDK. */
export const GOOGLE_WORKSPACE_ADAPTER = "google_workspace";

/** The largest service-account key accepted (a 2048- or 4096-bit RSA PEM fits well within). */
const MAX_PRIVATE_KEY = 8192;

/**
 * A Google Cloud service account's JSON key, as downloaded from the console. Only the four fields
 * the token grant needs are kept; the rest (project, client ID, URLs) are dropped. Never logged,
 * audited, stored in the database or returned: only `client_email` and `private_key_id` are.
 */
export const serviceAccountKeySchema = z.object({
  type: z.literal("service_account"),
  client_email: z.email().max(320),
  private_key_id: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{8,64}$/),
  private_key: z
    .string()
    .max(MAX_PRIVATE_KEY)
    .regex(/^-----BEGIN PRIVATE KEY-----\n[A-Za-z0-9+/=\n]+-----END PRIVATE KEY-----\n?$/),
});
export type ServiceAccountKey = z.infer<typeof serviceAccountKeySchema>;

/**
 * Whose users a Google Workspace source provisions (spec `directory` "Google provisioning
 * scope"): the members, direct or nested, of one group (by e-mail), or the users of one org unit
 * and its children (by path).
 */
export const googleProvisioningScopeSchema = z.union([
  z.strictObject({ group: z.email().max(320).toLowerCase() }),
  z.strictObject({
    orgUnit: z
      .string()
      .trim()
      .max(1000)
      .regex(/^\/\P{Cc}*$/u),
  }),
]);
export type GoogleProvisioningScope = z.infer<typeof googleProvisioningScopeSchema>;

/** The read privileges of the custom admin role a Google Workspace source needs. */
export const googleAdminPrivilegeSchema = z.enum(["users_read", "groups_read", "org_units_read"]);
export type GoogleAdminPrivilege = z.infer<typeof googleAdminPrivilegeSchema>;

/**
 * Why a pull run left an item out (not a failure): `outside_scope` (a membership of a Workspace
 * user outside the provisioning scope), `external_member` (a member outside the Workspace),
 * `customer_member` (Google's "all users" member).
 */
export const DIRECTORY_SKIP_REASONS = [
  "outside_scope",
  "external_member",
  "customer_member",
] as const;
export type DirectorySkipReason = (typeof DIRECTORY_SKIP_REASONS)[number];

// ── Sources ────────────────────────────────────────────────────────────────────────────────

/**
 * - `idp`: implicit, one per identity provider; the groups claim refreshes it at sign-in;
 * - `scim`: SCIM 2.0 provisioning (`/scim/v2`) with its own bearer token;
 * - `push`: the generic push API (`/api/directory`) with its own bearer token;
 * - `pull`: a connector the reconciliation job pulls through a `DirectorySource` adapter.
 */
export const directorySourceKindSchema = z.enum(["idp", "scim", "push", "pull"]);
export type DirectorySourceKind = z.infer<typeof directorySourceKindSchema>;

export const directorySourceStatusSchema = z.enum(["active", "disabled"]);
export type DirectorySourceStatus = z.infer<typeof directorySourceStatusSchema>;

/** A recent sync failure, kept on the source for the backoffice (newest first, capped). */
export const directoryErrorSchema = z.object({
  at: z.iso.datetime(),
  reason: z.string(),
  /** The external ID (or batch index) of the failed item; never a token or attribute value. */
  item: z.string().nullable(),
});
export type DirectoryError = z.infer<typeof directoryErrorSchema>;

/** How many recent failures a source keeps. */
export const DIRECTORY_ERRORS_KEPT = 20;

export const directorySourceSchema = z.object({
  id: directorySourceIdSchema,
  kind: directorySourceKindSchema,
  name: z.string(),
  status: directorySourceStatusSchema,
  /** Pull sources only: the reconciliation interval. */
  intervalMinutes: z.int().nullable(),
  /** Pull sources only: the adapter that reads the external system. */
  adapter: z.string().nullable(),
  /** Whether the source has a token (SCIM, push); the token itself is never returned. */
  hasToken: z.boolean(),
  lastSyncAt: z.iso.datetime().nullable(),
  lastFullAt: z.iso.datetime().nullable(),
  /** A pull source that has not completed a sync for longer than its interval. */
  stale: z.boolean(),
  /**
   * The tenant identity provider the source speaks for: an `idp` source's own, or the one a
   * `scim`, `push` or `pull` source records subjects for (null: none, it records no subject).
   * Optional for one release (older APIs omit it).
   */
  identityProviderId: z.string().nullable().optional(),
  /**
   * `idp` sources: users whose memberships from it have lapsed (no assertion within
   * `directory.idpGroupsMaxAge`); null for other kinds. Optional for one release.
   */
  lapsedUsers: z.int().nullable().optional(),
  errors: z.array(directoryErrorSchema),
  /**
   * Pull sources with a stored credential: who it is (a Google service account's e-mail and
   * key ID), never the key itself. Null without one. Optional for one release.
   */
  credential: z.object({ clientEmail: z.string(), keyId: z.string() }).nullable().optional(),
  /** Google Workspace sources: the provisioning scope (null for other adapters). */
  scope: googleProvisioningScopeSchema.nullable().optional(),
  /** Pull sources: the items the last run left out, counted by reason. Optional for one release. */
  skipped: z.record(z.string(), z.int()).nullable().optional(),
  createdAt: z.iso.datetime(),
});
export type DirectorySourceView = z.infer<typeof directorySourceSchema>;

const nameSchema = z.string().trim().min(1).max(200);

/** Default reconciliation interval of a pull source (design open question: 24 h). */
export const DEFAULT_PULL_INTERVAL_MINUTES = 24 * 60;

/** The tenant identity provider a source speaks for (`identity_providers.id`), or none. */
const sourceIdentityProviderSchema = z.uuid().nullable().optional();

export const createDirectorySourceRequestSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("scim"),
    name: nameSchema,
    identityProviderId: sourceIdentityProviderSchema,
  }),
  z.object({
    kind: z.literal("push"),
    name: nameSchema,
    identityProviderId: sourceIdentityProviderSchema,
  }),
  z.object({
    kind: z.literal("pull"),
    name: nameSchema,
    identityProviderId: sourceIdentityProviderSchema,
    adapter: z.string().trim().min(1).max(100),
    intervalMinutes: z
      .int()
      .min(15)
      .max(7 * 24 * 60)
      .default(DEFAULT_PULL_INTERVAL_MINUTES),
    /** Adapters that need one (Google Workspace): written once, never returned. */
    credential: serviceAccountKeySchema.optional(),
    /** Google Workspace: whose users the source provisions. */
    scope: googleProvisioningScopeSchema.optional(),
  }),
]);
export type CreateDirectorySourceRequest = z.infer<typeof createDirectorySourceRequestSchema>;

export const updateDirectorySourceRequestSchema = z
  .object({
    name: nameSchema.optional(),
    status: directorySourceStatusSchema.optional(),
    intervalMinutes: z
      .int()
      .min(15)
      .max(7 * 24 * 60)
      .optional(),
    identityProviderId: sourceIdentityProviderSchema,
    /** Google Workspace sources: a new provisioning scope, applied at the next run. */
    scope: googleProvisioningScopeSchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to update");
export type UpdateDirectorySourceRequest = z.infer<typeof updateDirectorySourceRequestSchema>;

/** A credential replaced on an existing pull source (rotation); written once, never returned. */
export const putSourceCredentialRequestSchema = z.object({ credential: serviceAccountKeySchema });

/** Why a directory source test failed, besides missing privileges. */
export const directorySourceTestErrorSchema = z.enum([
  "no_adapter",
  "credential_missing",
  "credential_refused",
  "token_refused",
  "scope_not_found",
  "throttled",
  "unreachable",
]);

/**
 * The result of testing a pull source (spec `tenant-administration` "Test before enabling"):
 * nothing is synced. `missing` names the read privileges the account lacks.
 */
export const directorySourceTestResultSchema = z.object({
  ok: z.boolean(),
  missing: z.array(googleAdminPrivilegeSchema),
  error: directorySourceTestErrorSchema.nullable(),
});
export type DirectorySourceTestResult = z.infer<typeof directorySourceTestResultSchema>;

/** Returned once, when a token is issued or rotated; later reads never show it. */
export const issuedSourceTokenSchema = z.object({
  source: directorySourceSchema,
  token: z.string(),
  /** SCIM sources: the base URL to configure in the identity provider. */
  scimBaseUrl: z.string().nullable(),
});
export type IssuedSourceToken = z.infer<typeof issuedSourceTokenSchema>;

// ── Attributes and rules ──────────────────────────────────────────────────────────────────

/** Attribute keys: identifiers, so they never need escaping anywhere. */
export const attributeKeySchema = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_.-]{0,63}$/, "Invalid attribute key");

/**
 * Reserved attribute: the user's external organisation code. It is always accepted (as a
 * string) and drives entity memberships through the org-code mapping, never by default.
 */
export const ORG_CODE_ATTRIBUTE = "orgCode";

export const attributeTypeSchema = z.enum(["string", "enum", "ordinal"]);
export type AttributeType = z.infer<typeof attributeTypeSchema>;

const enumValueSchema = z.string().trim().min(1).max(200);

export const attributeDefinitionSchema = z.discriminatedUnion("type", [
  z.object({ key: attributeKeySchema, type: z.literal("string") }),
  z.object({
    key: attributeKeySchema,
    type: z.literal("enum"),
    values: z
      .array(enumValueSchema)
      .min(1)
      .max(200)
      .refine((v) => new Set(v).size === v.length, "Duplicate enum values"),
  }),
  z.object({ key: attributeKeySchema, type: z.literal("ordinal") }),
]);
export type AttributeDefinition = z.infer<typeof attributeDefinitionSchema>;

export const putAttributeDefinitionRequestSchema = attributeDefinitionSchema.refine(
  (d) => d.key !== ORG_CODE_ATTRIBUTE,
  { message: `"${ORG_CODE_ATTRIBUTE}" is reserved`, path: ["key"] },
);

/** Largest value of an attribute, in characters (strings) or magnitude (ordinals). */
export const MAX_ATTRIBUTE_TEXT = 500;
export const MAX_ORDINAL = 1_000_000_000;

/** A value as sources send it; its declared type decides whether it is accepted. */
export const attributeValueSchema = z.union([z.string().max(MAX_ATTRIBUTE_TEXT), z.number()]);
export type AttributeValue = z.infer<typeof attributeValueSchema>;

/** A stored, typed value. */
export type TypedAttributeValue = { text: string; int: null } | { text: null; int: number };

/**
 * Types `value` against its definition: `string` and `enum` take strings (an `enum` one of its
 * values), `ordinal` takes an integer. Returns the reason otherwise (sync rejects the value and
 * keeps the previous one).
 */
export function typeAttributeValue(
  definition: AttributeDefinition,
  value: AttributeValue,
): TypedAttributeValue | { reason: string } {
  switch (definition.type) {
    case "string":
      return typeof value === "string" && value.length <= MAX_ATTRIBUTE_TEXT
        ? { text: value, int: null }
        : { reason: `${definition.key} expects a string` };
    case "enum":
      return typeof value === "string" && definition.values.includes(value)
        ? { text: value, int: null }
        : { reason: `${definition.key} expects one of its declared values` };
    case "ordinal":
      return typeof value === "number" && Number.isInteger(value) && Math.abs(value) <= MAX_ORDINAL
        ? { text: null, int: value }
        : { reason: `${definition.key} expects an integer` };
  }
}

/**
 * One predicate of a rule. Typed and closed: `eq`, `in` on `string` and `enum` attributes,
 * `gte`, `lte` on `ordinal` ones. There is no OR and no negation: a negation would match users
 * whose attribute is merely missing or not yet synced, and two rules on one document already
 * express OR (readers are a union). Unknown keys are rejected (`strict`).
 */
export const attributePredicateSchema = z.discriminatedUnion("op", [
  z.strictObject({ key: attributeKeySchema, op: z.literal("eq"), value: enumValueSchema }),
  z.strictObject({
    key: attributeKeySchema,
    op: z.literal("in"),
    value: z.array(enumValueSchema).min(1).max(100),
  }),
  z.strictObject({
    key: attributeKeySchema,
    op: z.literal("gte"),
    value: z.int().min(-MAX_ORDINAL).max(MAX_ORDINAL),
  }),
  z.strictObject({
    key: attributeKeySchema,
    op: z.literal("lte"),
    value: z.int().min(-MAX_ORDINAL).max(MAX_ORDINAL),
  }),
]);
export type AttributePredicate = z.infer<typeof attributePredicateSchema>;

/** A rule is the conjunction of its predicates; it needs at least one (none would match all). */
export const attributeRuleInputSchema = z.strictObject({
  name: nameSchema,
  predicates: z.array(attributePredicateSchema).min(1).max(20),
});
export type AttributeRuleInput = z.infer<typeof attributeRuleInputSchema>;

export const attributeRuleSchema = attributeRuleInputSchema.extend({
  id: attributeRuleIdSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type AttributeRule = z.infer<typeof attributeRuleSchema>;

/**
 * Checks predicates against the tenant's definitions: every key declared, and each operator
 * valid for the key's type (`enum` values among the declared ones). Returns the problems.
 */
export function ruleProblems(
  definitions: readonly AttributeDefinition[],
  predicates: readonly AttributePredicate[],
): string[] {
  const byKey = new Map(definitions.map((d) => [d.key, d]));
  const problems: string[] = [];
  for (const p of predicates) {
    // `orgCode` drives entity memberships, not rules: it is never a declared attribute.
    const definition = byKey.get(p.key) ?? null;
    if (!definition) {
      problems.push(`${p.key} is not a declared attribute`);
      continue;
    }
    const ordinal = definition.type === "ordinal";
    if ((p.op === "gte" || p.op === "lte") !== ordinal) {
      problems.push(`${p.op} cannot be used on ${definition.type} attribute ${p.key}`);
      continue;
    }
    if (definition.type === "enum") {
      const values = p.op === "in" ? p.value : [p.value as string];
      if (values.some((v) => !definition.values.includes(v))) {
        problems.push(`${p.key} has no such value`);
      }
    }
  }
  return problems;
}

/** Whether a rule uses `key` (with `value`, for an enum value removal). */
export function ruleUses(rule: AttributeRuleInput, key: string, value?: string): boolean {
  return rule.predicates.some(
    (p) =>
      p.key === key &&
      (value === undefined ||
        (p.op === "in" ? p.value.includes(value) : p.op === "eq" && p.value === value)),
  );
}

// ── Groups ────────────────────────────────────────────────────────────────────────────────

export const groupSchema = z.object({
  id: groupIdSchema,
  displayName: z.string(),
  externalId: z.string(),
  sourceId: directorySourceIdSchema,
  sourceKind: directorySourceKindSchema,
  /** The entity the group is scoped to, if any. */
  entityId: entityIdSchema.nullable(),
  /** Direct members (users and groups), every source counted. */
  memberCount: z.int(),
  createdAt: z.iso.datetime(),
});
export type Group = z.infer<typeof groupSchema>;

export const renameGroupRequestSchema = z.object({ displayName: nameSchema });

/** Groups and rules a readers or members picker can offer (tenant-wide, names only). */
export const principalOptionsSchema = z.object({
  groups: z.array(z.object({ id: groupIdSchema, displayName: z.string() })),
  rules: z.array(z.object({ id: attributeRuleIdSchema, name: z.string() })),
});
export type PrincipalOptions = z.infer<typeof principalOptionsSchema>;

// ── Org codes ─────────────────────────────────────────────────────────────────────────────

export const orgCodeSchema = z.string().trim().min(1).max(100);

export const orgCodeMappingSchema = z.object({
  id: z.string(),
  /** Null: applies to every source. */
  sourceId: directorySourceIdSchema.nullable(),
  code: z.string(),
  entityId: entityIdSchema,
  createdAt: z.iso.datetime(),
});
export type OrgCodeMapping = z.infer<typeof orgCodeMappingSchema>;

export const putOrgCodeMappingRequestSchema = z.object({
  code: orgCodeSchema,
  entityId: z.uuid(),
  sourceId: z.uuid().nullable().default(null),
});
export type PutOrgCodeMappingRequest = z.infer<typeof putOrgCodeMappingRequestSchema>;

export const unmappedEntrySchema = z.object({
  userId: userIdSchema,
  displayName: z.string().nullable(),
  sourceId: directorySourceIdSchema,
  code: z.string(),
  since: z.iso.datetime(),
});
export type UnmappedEntry = z.infer<typeof unmappedEntrySchema>;

// ── Records and push ──────────────────────────────────────────────────────────────────────

const externalIdSchema = z.string().trim().min(1).max(256);

/**
 * A user as a source describes it. `attributes`: values by key, `null` to clear one; undeclared
 * keys are ignored, mistyped values rejected with a reason (the previous value stays).
 */
export const directoryUserRecordSchema = z.object({
  externalId: externalIdSchema,
  email: z.email().max(320),
  displayName: z.string().trim().max(200).nullable().optional(),
  active: z.boolean().default(true),
  /** The user's subject at the tenant's identity provider, to bind their first sign-in. */
  idpSubject: z.string().trim().min(1).max(256).nullable().optional(),
  attributes: z.record(z.string().max(64), attributeValueSchema.nullable()).optional(),
});
export type DirectoryUserRecord = z.input<typeof directoryUserRecordSchema>;
export type ParsedDirectoryUserRecord = z.output<typeof directoryUserRecordSchema>;

export const directoryGroupRecordSchema = z.object({
  externalId: externalIdSchema,
  displayName: z.string().trim().min(1).max(200),
  /** The entity the group is scoped to (an entity of the tenant). */
  entityId: z.uuid().nullable().optional(),
});
export type DirectoryGroupRecord = z.infer<typeof directoryGroupRecordSchema>;

export const directoryMemberRefSchema = z.object({
  type: z.enum(["user", "group"]),
  externalId: externalIdSchema,
});
export type DirectoryMemberRef = z.infer<typeof directoryMemberRefSchema>;

export const directoryMembershipRecordSchema = z.object({
  groupExternalId: externalIdSchema,
  member: directoryMemberRefSchema,
});
export type DirectoryMembershipRecord = z.infer<typeof directoryMembershipRecordSchema>;

/** What a pull adapter streams for a full snapshot. */
export const directoryRecordSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("user"), user: directoryUserRecordSchema }),
  z.object({ type: z.literal("group"), group: directoryGroupRecordSchema }),
  z.object({ type: z.literal("membership"), membership: directoryMembershipRecordSchema }),
  /** An item the adapter left out on purpose, counted on the source (never an identifier). */
  z.object({ type: z.literal("skipped"), reason: z.enum(DIRECTORY_SKIP_REASONS) }),
]);
export type DirectoryRecord = z.input<typeof directoryRecordSchema>;

/** One change of a push batch. Each item succeeds or fails on its own. */
export const directoryPushItemSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("upsertUser"), user: directoryUserRecordSchema }),
  z.object({ op: z.literal("deactivateUser"), externalId: externalIdSchema }),
  z.object({
    op: z.literal("setAttributes"),
    userExternalId: externalIdSchema,
    attributes: z.record(z.string().max(64), attributeValueSchema.nullable()),
  }),
  z.object({ op: z.literal("upsertGroup"), group: directoryGroupRecordSchema }),
  z.object({ op: z.literal("deleteGroup"), externalId: externalIdSchema }),
  z.object({ op: z.literal("addMember"), membership: directoryMembershipRecordSchema }),
  z.object({ op: z.literal("removeMember"), membership: directoryMembershipRecordSchema }),
]);
export type DirectoryPushItem = z.input<typeof directoryPushItemSchema>;

/** Items per push request: they apply one by one, so a request stays within seconds. */
export const MAX_PUSH_ITEMS = 1000;

export const directoryPushBatchSchema = z.object({
  items: z.array(directoryPushItemSchema).min(1).max(MAX_PUSH_ITEMS),
});

/** Full replacement of one user's groups from the calling source. */
export const setUserGroupsRequestSchema = z.object({
  groups: z.array(externalIdSchema).max(MAX_PUSH_ITEMS),
});

export const MAX_SNAPSHOT_RECORDS = 200_000;

/** A full snapshot: the source's users, groups and memberships become exactly these. */
export const directorySnapshotSchema = z
  .object({
    users: z.array(directoryUserRecordSchema).default([]),
    groups: z.array(directoryGroupRecordSchema).default([]),
    memberships: z.array(directoryMembershipRecordSchema).default([]),
  })
  .refine(
    (s) => s.users.length + s.groups.length + s.memberships.length <= MAX_SNAPSHOT_RECORDS,
    "Snapshot too large",
  );
export type DirectorySnapshot = z.input<typeof directorySnapshotSchema>;

/**
 * Why one item failed: `unknown_user`, `unknown_group` (not known to this source),
 * `unknown_entity`, `cycle` (the membership would make a group contain itself),
 * `depth_exceeded` (nesting deeper than {@link MAX_GROUP_DEPTH}), `invalid_attribute`
 * (a value of the wrong type), `conflict` (e.g. an e-mail of another user), `invalid`, and
 * `seat_limit` (creating or re-activating the user would exceed the tenant's seats).
 */
export const DIRECTORY_FAILURE_CODES = [
  "unknown_user",
  "unknown_group",
  "unknown_entity",
  "cycle",
  "depth_exceeded",
  "invalid_attribute",
  "conflict",
  "invalid",
  "seat_limit",
] as const;
export const directoryFailureSchema = z.enum(DIRECTORY_FAILURE_CODES);
/** Source-level failures recorded alongside item-level failures in the sync log. */
export const DIRECTORY_SYNC_FAILURE_CODES = [
  ...DIRECTORY_FAILURE_CODES,
  "no_adapter",
  "snapshot_too_large",
  "pull_failed",
  "invalid_records",
  "credential_missing",
  "credential_refused",
  "token_refused",
  "missing_privilege",
  "scope_not_found",
  "throttled",
] as const;
export type DirectorySyncFailure = (typeof DIRECTORY_SYNC_FAILURE_CODES)[number];
export type DirectoryFailure = z.infer<typeof directoryFailureSchema>;

/** Deepest group nesting accepted. */
export const MAX_GROUP_DEPTH = 16;

/**
 * `directory.idpGroupsMaxAge`, in hours: memberships an identity provider asserted lapse once
 * the user's last assertion is older (spec `directory`). 168 is the former worst case (the web
 * session's absolute cap); the default matches the web idle timeout.
 */
export const IDP_GROUPS_MAX_AGE_HOURS = { min: 1, max: 168, default: 12 } as const;

/**
 * What an item that applied left out: `idp_subject_unbound`, an `idpSubject` from a source that
 * names no identity provider (it records no subject).
 */
export const directoryWarningSchema = z.enum(["idp_subject_unbound"]);
export type DirectoryWarning = z.infer<typeof directoryWarningSchema>;

export const directoryItemResultSchema = z.object({
  index: z.int(),
  status: z.enum(["ok", "failed"]),
  reason: directoryFailureSchema.optional(),
  /** Rejected attribute values of an item that otherwise applied (`invalid_attribute`). */
  rejected: z.array(z.object({ key: z.string(), reason: z.string() })).optional(),
  warnings: z.array(directoryWarningSchema).optional(),
});
export type DirectoryItemResult = z.infer<typeof directoryItemResultSchema>;

export const directoryBatchResultSchema = z.object({
  results: z.array(directoryItemResultSchema),
  created: z.int(),
  updated: z.int(),
  removed: z.int(),
  failed: z.int(),
});
export type DirectoryBatchResult = z.infer<typeof directoryBatchResultSchema>;

// ── Project grants ────────────────────────────────────────────────────────────────────────

export const projectGroupGrantSchema = z.object({
  groupId: groupIdSchema,
  displayName: z.string(),
  role: assignableProjectRoleSchema,
  /** Users in the group, transitively. */
  memberCount: z.int(),
  /** Members the outside-entity rule keeps out of the project (0 when sharing is allowed). */
  excludedCount: z.int(),
  createdAt: z.iso.datetime(),
});
export type ProjectGroupGrant = z.infer<typeof projectGroupGrantSchema>;

export const addProjectGroupRequestSchema = z.object({
  groupId: z.uuid(),
  role: assignableProjectRoleSchema,
});
export type AddProjectGroupRequest = z.infer<typeof addProjectGroupRequestSchema>;

/** A project role granted to an attribute rule: every user matching it acts with the role. */
export const projectRuleGrantSchema = z.object({
  ruleId: attributeRuleIdSchema,
  name: z.string(),
  role: assignableProjectRoleSchema,
  createdAt: z.iso.datetime(),
});
export type ProjectRuleGrant = z.infer<typeof projectRuleGrantSchema>;

/**
 * The role of a group or attribute-rule grant (`PUT …/members/groups/:groupId`,
 * `PUT …/rule-grants/:ruleId`): never `owner` (→ 422).
 */
export const setProjectGrantRoleRequestSchema = z.object({ role: assignableProjectRoleSchema });
